"use strict";

import {createRegularPolygon, createBox} from 'ol/interaction/Draw';
import {unByKey} from 'ol/Observable';
import * as olCondition from 'ol/events/condition';
import Draw from 'ol/interaction/Draw';
import Polygon from "ol/geom/Polygon";
import LineString from "ol/geom/LineString";

/**
 * @classdesc
 * Openlayers Implementierung für Kartenaktionen.
 *
 * @abstract
 * @class
 * @extends gc.gis.map.GCMapDrawInteraction
 * @memberof gc.gis.map
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 *
 * @param {gc.gis.map.GCMapView} map Karte, der die Interaktion gehört
 * @param {Object} [opt_options] optionale Aufrufoptionen
 * @param {gc.gis.style.GCAbstractStyle[]} [opt_options.drawingStyle] Darstellungseigenschaften während des Zeichnens
 * @param {gc.gis.style.GCAbstractStyle[]} [opt_options.finalStyle] Darstellungseigenschaften für fertige Objekte
 * @param {boolean} [opt_options.allowClearDrawingArea] Flag welches anzeigt, ob die Inhalte des korrespondierenden
 *      Tracking-Layers durch die Funktion "Zeichenfläche leeren", verändert werden dürfen
 * @param {boolean} [opt_options.freehand=false] Freihandzeichnen aktivieren
 */
gc.gis.map.OLMapDrawInteraction = function(map, opt_options)
{
    gc.gis.map.GCMapDrawInteraction.call(this, map, opt_options);

    opt_options = opt_options || {};

    this._drawingStyle = opt_options.drawingStyle || [];
    this._finalStyle = opt_options.finalStyle || [];
	this._allowClearDrawingArea = opt_options.allowClearDrawingArea || false;
	this._freehand = opt_options.freehand === true;

	this._init();
};
gc.gis.map.OLMapDrawInteraction.prototype = Object.create(gc.gis.map.GCMapDrawInteraction.prototype);


/**
 * Initialisiert die Karteninteraktion.
 *
 * @protected
 * @method
 */
gc.gis.map.OLMapDrawInteraction.prototype._init = function()
{
	/**
	 * @type {ol.Map}
	 * @private
	 */
	this._ol_map = this.map.getMap().getImpl();

	/**
	 * @type {ol.interaction.Draw}
	 * @private
	 */
	this._draw_interaction = null;

	/**
	 * Trackinglayer für die Zeichnen-Ergebnisse
	 * @type {gc.gis.layer.OLTrackingLayer}
	 * @private
	 */
    this._layer = new gc.gis.layer.OLTrackingLayer(this.map.getMap(), {
    	allowClearDrawingArea: this._allowClearDrawingArea
    });

    if (this._finalStyle)
    {
        this._layer.setStyle(this._finalStyle);
    }
    this.map.getMap().addTrackingLayer(this._layer);

    this._createDrawInteraction(gc.gis.geom.GCGeometryType.POINT);
};

/**
 * @inheritdoc
 */
gc.gis.map.OLMapDrawInteraction.prototype.getLayer = function()
{
	return this._layer;
};

/**
 * @inheritdoc
 */
gc.gis.map.OLMapDrawInteraction.prototype.getImpl = function()
{
    return this._draw_interaction;
};

/**
 * @inheritdoc
 */
gc.gis.map.OLMapDrawInteraction.prototype.setGeometryType = function(geometryType)
{
   this._createDrawInteraction(geometryType);
};

/**
 * @inheritdoc
 */
gc.gis.map.OLMapDrawInteraction.prototype.getGeometryType = function()
{
	return this._geometryType;
};

/**
 * @inheritdoc
 */
gc.gis.map.OLMapDrawInteraction.prototype.enableFreehand = function(enable)
{
	this._freehand = enable === true;
};

/**
 * @inheritdoc
 */
gc.gis.map.OLMapDrawInteraction.prototype.reset = function()
{
    this._layer.clear();
};

/**
 * @inheritdoc
 */
gc.gis.map.OLMapDrawInteraction.prototype.setActive = function(state)
{
	state ? this._activate() : this._deactivate();
};

/**
 * @inheritdoc
 */
gc.gis.map.OLMapDrawInteraction.prototype.isActive = function()
{
    return this._draw_interaction.getActive() === true;
};

/**
 * @inheritdoc
 */
gc.gis.map.OLMapDrawInteraction.prototype.clear = function()
{
	this._layer.clear();
};

/**
 * Führt die Aktiviert der Interaktion aus.
 *
 * @private
 * @method
 */
gc.gis.map.OLMapDrawInteraction.prototype._activate = function()
{
	if (!this.isActive())
	{
		this._draw_interaction.setActive(true);
	}
};

/**
 * Führt die Deaktiviert der Interaktion aus.
 *
 * @private
 * @method
 */
gc.gis.map.OLMapDrawInteraction.prototype._deactivate = function()
{
	if (this.isActive())
	{
		this._draw_interaction.setActive(false);
	}
};

/**
 * Initialisiert die Karteninteraktion zum Zeichnen.
 *
 * @private
 * @method
 * @param {gc.gis.geom.GCGeometryType} geometryType Geometrietyp
 */
gc.gis.map.OLMapDrawInteraction.prototype._createDrawInteraction = function(geometryType)
{
    const self = this;
    let state = false;
    const props = {
        source: this._layer.getImpl().getSource()
    };

	this._geometryType = geometryType;

    if (this._drawingStyle)
    {
        props.style = gc.gis.OLUtil.toOLStyle(this._drawingStyle);
    }

    if (this._draw_interaction) {
        state = this._draw_interaction.getActive();
        this._ol_map.removeInteraction(this._draw_interaction);
    }

    switch (geometryType) {
        case gc.gis.geom.GCGeometryType.POLYGON:
            props.type = 'Polygon';
            break;
        case gc.gis.geom.GCGeometryType.BOX:
            props.type = 'Circle';
            props.geometryFunction = createBox();
            break;
        case gc.gis.geom.GCGeometryType.RECTANGLE:
            props.type = 'Circle';
            props.geometryFunction = createRegularPolygon(4);
            break;
	    case gc.gis.geom.GCGeometryType.LINESTRING:
		    props.type = 'LineString';
		    break;
	    case gc.gis.geom.GCGeometryType.CIRCLE:
		    props.type = 'Circle';
		    break;
	    case 'PolygonOrLinestring': // spezieller Typ für kombinierte Polygon/Linienerfassung (z.B. Messfunktion)
		    props.type = 'Polygon';
		    props.minPoints = 2;
		    break;
        default:
            props.type = 'Point';
    }

    props.freehandCondition = this._createFreehandFunc();

    this._draw_interaction = new Draw(props);
    this._draw_interaction.set('_is_gc', true, true);
    this._draw_interaction.setActive(state);

    this._ol_map.addInteraction(this._draw_interaction);

	let isSnappedOnFirst = false,
		featureChangeEvtentKey,
		numPoints = 1;
    this._draw_interaction.on('drawstart', function(evt) {

	    if (geometryType === 'PolygonOrLinestring')
	    {
		    featureChangeEvtentKey = evt.feature.on('change', function(evt2) {
			    let geom = evt2.target.getGeometry();
			    // das sketchPolygon ist stets geschlossen, daher die letzte Koordinate löschen
			    const coords = geom.getCoordinates()[0].slice(0, -1);

			    if (geom instanceof Polygon)
			    {
				    // eine neue Koordinate wurde hinzugefügt oder gelöscht => den aktuellen Snap-Status beibehalten
				    if (numPoints !== coords.length)
				    {
					    numPoints = coords.length;
				    }
				    else if (coords.length > 1)
				    {
					    const firstPoint = coords[0];
					    const lastPoint = coords[coords.length - 1];
					    isSnappedOnFirst = (lastPoint[0] === firstPoint[0] && lastPoint[1] === firstPoint[1]);
				    }
			    }
		    });
	    }

        self._fireEvent(gc.gis.map.GCMapDrawInteractionEvent.DRAWSTART, {
	        feature: new gc.gis.feature.OLFeature(evt.feature)
        });
    });

    this._draw_interaction.on('drawend', function(evt) {
	    const ol_feature = evt.feature;

	    // im Modus 'PolygonOrLinestring', ggf. die schließende Koordinate entfernen
	    if (geometryType === 'PolygonOrLinestring')
	    {
		    let geom = ol_feature.getGeometry();
		    unByKey(featureChangeEvtentKey);

	    	if (geom instanceof Polygon && !isSnappedOnFirst)
		    {
			    // aus Polygon mache LineString
			    const coords = geom.getCoordinates()[0].slice(0, -1);
			    geom = new LineString(coords);
			    ol_feature.setGeometry(geom);
		    }
	    }

	    const feature = new gc.gis.feature.OLFeature(ol_feature);
	    feature.getGeometry().setCRS(self.map.getProjection());
        self._fireEvent(gc.gis.map.GCMapDrawInteractionEvent.DRAWEND, {
            feature: feature,
	        event: evt
        });
    });
};

/**
 * Erzeugt eine Funktion welche anzeigt, ob Freihandzeichnen aktiv ist oder nicht.
 *
 * @return {Function}
 * @private
 */
gc.gis.map.OLMapDrawInteraction.prototype._createFreehandFunc = function()
{
	const self = this;
	/**
	 * @param {MapBrowserEvent} mapBrowserEvent Map browser event.
	 */
	return function (mapBrowserEvent) {
		return self._freehand === true || olCondition.shiftKeyOnly(mapBrowserEvent);
	}
};